import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoMdCall } from 'react-icons/io';
import { stopCallNotification } from '../utils/audioNotification';
import { clearIncomingCallData } from '../utils/callUtils';
import { deleteRoom } from '../services/roomService';
import { logger } from '../utils/logger';

/**
 * Global incoming call notification button shown in header
 * Appears when there's an incoming call and allows user to navigate to call page
 */
export function IncomingCallNotification() {
  const navigate = useNavigate();
  const [incomingCall, setIncomingCall] = useState<{
    callerPhone: string;
    callerId: string;
    timestamp: number;
  } | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check for incoming call in sessionStorage
  useEffect(() => {
    const checkIncomingCall = () => {
      const incomingCallStatus = sessionStorage.getItem('incomingCallStatus');
      const incomingCallRoomName = sessionStorage.getItem('incomingCallRoomName');
      const selectedCustomer = sessionStorage.getItem('selectedCustomer');

      if (incomingCallStatus && 
          incomingCallStatus !== 'answered' && 
          incomingCallStatus !== 'ended' &&
          incomingCallStatus !== 'rejected' &&
          incomingCallRoomName &&
          selectedCustomer) {
        try {
          const customer = JSON.parse(selectedCustomer);
          const now = Date.now();
          
          // Check if this is a new incoming call (different from current one)
          const isNewCall = !incomingCall || 
                           incomingCall.callerPhone !== customer.phone ||
                           (now - incomingCall.timestamp) > 1000; // More than 1 second difference
          
          if (isNewCall) {
            setIncomingCall({
              callerPhone: customer.phone || '',
              callerId: customer.name || customer.phone || '',
              timestamp: now,
            });
            
            // Clear any existing timeout
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
            
            // Set timeout to auto-reject after 60 seconds
            timeoutRef.current = setTimeout(() => {
              logger.log('Auto-rejecting call after 60 seconds timeout');
              handleDismiss();
            }, 60000); // 60 seconds
          }
        } catch (error) {
          // Ignore parse errors
        }
      } else {
        // Clear timeout if call is answered/rejected/ended
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setIncomingCall(null);
      }
    };

    // Check immediately
    checkIncomingCall();

    // Check periodically for incoming calls
    const interval = setInterval(checkIncomingCall, 1000);

    return () => {
      clearInterval(interval);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [incomingCall]);

  const handleNavigateToCall = () => {
    if (!incomingCall) return;

    // Stop ringtone immediately
    stopCallNotification();

    const callerPhoneClean = incomingCall.callerPhone.replace(/[^\d]/g, '');
    const targetPath = `/events/whatsapp-send-message/${callerPhoneClean}/whatsappcalls`;
    
    // Set a flag to auto-join the call when the page loads
    sessionStorage.setItem('autoJoinIncomingCall', 'true');
    
    // Navigate to the call page
    navigate(targetPath);
    
    // Dispatch event to trigger join call if already on the page
    window.dispatchEvent(new CustomEvent('answerIncomingCall'));
  };

  const handleDismiss = async () => {
    // Clear timeout if user manually rejects
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Stop ringtone immediately
    stopCallNotification();
    
    // Get room name before clearing data
    const incomingCallRoomName = sessionStorage.getItem('incomingCallRoomName');
    
    // Clear incoming call data
    clearIncomingCallData();
    setIncomingCall(null);
    
    // Delete the room and remove all users
    if (incomingCallRoomName) {
      try {
        await deleteRoom(incomingCallRoomName);
        logger.log('Room deleted after rejecting call:', incomingCallRoomName);
      } catch (error) {
        logger.error('Error deleting room after rejecting call:', error);
        // Continue even if room deletion fails
      }
    }
  };

  if (!incomingCall) {
    return null;
  }

  // Show notification everywhere - removed condition to hide on call page
  // User should be able to see incoming call notification regardless of current page

  return (
    <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2 animate-pulse">
      <div className="flex items-center gap-2">
        <IoMdCall className="w-4 h-4 text-green-600" />
        <span className="text-sm font-medium text-green-800">
          Incoming call from {incomingCall.callerId}
        </span>
      </div>
      <button
        onClick={handleNavigateToCall}
        className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 transition-colors"
      >
        Answer
      </button>
      <button
        onClick={handleDismiss}
        className="px-3 py-1 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition-colors"
        title="Reject call"
      >
        Reject
      </button>
    </div>
  );
}
